import { Children, type ReactNode } from 'react';

import { Card, CardContent, CardHeader } from '../../ui/card';
import { cn } from '../../ui/utils';
import { Sparkline, type Trend } from '../chart/Sparkline';

export type { Trend };

const trendColor: Record<Trend, string> = {
  up: 'text-primary-ink',
  down: 'text-destructive',
  flat: 'text-muted-foreground',
};

export function StatCard(props: {
  label: string;
  value: ReactNode;
  /** A plain caveat on the value, e.g. its unit or what it excludes. */
  hint?: string;
  /** Names what `chip`'s comparison is against, e.g. "vs. the last 7 days". */
  caption?: ReactNode;
  chip?: ReactNode;
  delta?: ReactNode;
  trend?: Trend;
  sparkline?: readonly number[];
  aside?: ReactNode;
}): ReactNode {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-2 pb-2">
        <p className="text-label text-muted-foreground">{props.label}</p>
        {props.aside}
      </CardHeader>
      <CardContent className="flex flex-col gap-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span data-numeric className="text-title-lg">
            {props.value}
          </span>
          {props.delta !== undefined && (
            <span
              data-numeric
              className={
                props.trend !== undefined
                  ? `text-body-sm ${trendColor[props.trend]}`
                  : 'text-body-sm'
              }
            >
              {props.delta}
            </span>
          )}
          {props.chip}
          {props.sparkline !== undefined && (
            <div className="ml-auto shrink-0">
              <Sparkline
                values={props.sparkline}
                tone={props.trend ?? 'flat'}
                label={props.label}
              />
            </div>
          )}
        </div>
        {props.hint !== undefined && (
          <p className="text-caption text-muted-foreground">{props.hint}</p>
        )}
        {props.caption !== undefined && (
          <p className="text-caption text-muted-foreground">{props.caption}</p>
        )}
      </CardContent>
    </Card>
  );
}

export function StatGrid(props: { children: ReactNode }): ReactNode {
  // A five-card row only widens to five columns at 1440: fewer cards keep
  // the regular four-column track instead of stretching thin across an
  // unused fifth column.
  const five = Children.count(props.children) === 5;
  return (
    <div
      className={cn(
        'grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4',
        five && '1440:grid-cols-5',
      )}
    >
      {props.children}
    </div>
  );
}
